import { Link } from 'react-router-dom'

import compact from 'lodash/compact'
import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Emoji from 'modules/calendar/components/Emoji'
import type {
  CalendarEmployeeData,
  CalendarEmployeeLookup,
} from 'modules/calendar/lib/calendarEmployeeData'
import { calendarEventCanSeeTimeoffTypes } from 'modules/calendar/lib/calendarLeaves'
import type CalendarEvent from 'modules/calendar/store/models/CalendarEvent'

import emojiMap from 'modules/calendar/components/Calendar/components/emojiMap'
import Bubble from './Bubble'
import EventCompanyHoliday from './Event/CompanyHoliday'
import EmployeeEvent from './Event/Employee'
import PostEvent from './Event/PostEvent'
import styles from './index.module.css'

const getHalfDayTypeLabel = (type: string) => {
  switch (type) {
    case 'beggining_of_day':
      return i18n.t('calendar.half_day_type.beggining_of_day')
    case 'end_of_day':
      return i18n.t('calendar.half_day_type.end_of_day')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface Props {
  calendarEvents: CalendarEvent[]
  withDates?: boolean
  canEditBankHolidays: boolean
  employeeLookup: CalendarEmployeeLookup
}

const DayEventsDetail = ({
  calendarEvents,
  withDates,
  canEditBankHolidays,
  employeeLookup,
}: Props) => {
  const { navigation } = useNavigation()
  const {
    company: { name: companyName },
    employee: { id: currentEmployeeId },
  } = useCurrentSession()

  const canReadProfile = (emp: CalendarEmployeeData) => {
    return currentEmployeeId === emp.id || (emp._policies?.readLeaves.can ?? false)
  }

  const employeeToName = (emp: CalendarEmployeeData) => {
    const lastName = emp.lastName?.trim() || ''
    const firstName = emp.firstName?.trim() || ''
    const canSharePreferred = emp._policies?.sharePreferredName.can ?? false
    const preferred = !canSharePreferred ? firstName : emp.preferredName || firstName
    const shortName = lastName ? `${preferred} ${lastName[0]}.` : preferred
    if (canReadProfile(emp)) {
      return (
        <Link to={navigation.employees.id.fullPath({ id: emp.id })} className={styles.employeeLink}>
          <span>{shortName}</span>
        </Link>
      )
    }

    return (
      <div className={styles.employee}>
        <span>{shortName}</span>
      </div>
    )
  }

  const commonEvent = (kind: string) => {
    const eventCases = (event: CalendarEvent) => {
      switch (kind) {
        case 'companyHoliday':
          return (
            <EventCompanyHoliday
              key={event.id}
              id={event.attributes.metadata.company_holiday_id}
              event={event}
              canEditBankHolidays={canEditBankHolidays}
            />
          )

        case 'postEvent':
          return <PostEvent key={event.id} event={event} />

        default:
          return (
            <EmployeeEvent
              key={event.id}
              employeeId={event.metadata('employee_id')}
              event={event}
              withDates={Boolean(withDates)}
              employeeLookup={employeeLookup}
            />
          )
      }
    }

    return eventCases
  }

  const getKindLabel = (kind: string, params: Record<string, unknown>) => {
    switch (kind) {
      case 'firstDay':
        return i18n.tp('calendar.firstDay', params)
      case 'holiday':
        return i18n.tp('calendar.holiday', params)
      case 'companyHoliday':
        return i18n.tp('calendar.companyHoliday', params)
      case 'birthday':
        return i18n.tp('calendar.birthday', params)
      default:
        return i18n.tp('calendar.other', params)
    }
  }

  const otherEventGroup = (events: CalendarEvent[], kind: string) => {
    const emojiName = emojiMap[kind]

    return (
      <div key={kind} className={styles.event}>
        <div className={styles.emoji}>{emojiName && <Emoji name={emojiName} size="small" />}</div>
        <div className={styles.description}>
          <strong className={styles.title}>{getKindLabel(kind, { count: events.length })}</strong>
          {compact(events.map(commonEvent(kind)))}
        </div>
      </div>
    )
  }

  const workiversaryEvents = (events: CalendarEvent[] | null | undefined) => {
    if (!events) return null

    if (isEmpty(events)) return null

    return <Bubble overflowLimit={2}>{events.map((event) => workiversaryEvent(event))}</Bubble>
  }

  const otherEvents = (events: CalendarEvent[] | null | undefined) => {
    if (!events) return null

    if (isEmpty(events)) return null

    const groupedEvents = groupBy(events, (event) => event.attributes.type)

    return <Bubble overflowLimit={4}>{map(groupedEvents, otherEventGroup)}</Bubble>
  }

  const workiversaryEvent = (event: CalendarEvent) => {
    const empId = event.metadata('employee_id')
    const emp = employeeLookup.get(empId)

    if (!emp) return

    const { count } = event.attributes.metadata
    const day = moment(event.attributes.date).format('dddd')
    const emojiName = emojiMap.workiversary

    return (
      <div className={styles.event} key={event.id}>
        <div className={styles.emoji}>{emojiName && <Emoji name={emojiName} size="small" />}</div>
        <div className={styles.description}>
          {employeeToName(emp)}{' '}
          {i18n.tp('calendar.workiversary_description', {
            count,
            companyName: companyName,
            day,
          })}
        </div>
      </div>
    )
  }

  const leaveType = (event: CalendarEvent) => {
    return calendarEventCanSeeTimeoffTypes(event, employeeLookup) ? event.leaveType : 'default'
  }

  const leaveTypeLabel = (event: CalendarEvent) => {
    const params = {
      count: 1,
    }

    if (!calendarEventCanSeeTimeoffTypes(event, employeeLookup)) {
      return i18n.tp('calendar.default', params)
    }

    switch (event.leaveType) {
      case 'holiday':
        return i18n.tp('calendar.holiday', params)
      case 'sick':
        return i18n.tp('calendar.sick', params)
      case 'other':
      default:
        return i18n.tp('calendar.other', params)
    }
  }

  const leaveCopyParams = (event: CalendarEvent) => {
    const { start_on: startOn, finish_on: finishOn, half_day: halfDay } = event.attributes.metadata

    const date = stringToDate(startOn, 'backend').format('ll')

    const baseParams = {
      type: leaveTypeLabel(event),
    }

    if (halfDay) {
      const halfDayType = getHalfDayTypeLabel(halfDay ?? '')

      return { ...baseParams, date, half_day: halfDayType }
    }

    return {
      ...baseParams,
      from_time: date,
      to_time: finishOn ? stringToDate(finishOn, 'backend').format('ll') : null,
    }
  }

  const leaveCopy = (event: CalendarEvent): string => {
    const params = leaveCopyParams(event)
    const { finish_on: finishOn, half_day: halfDay, approved } = event.attributes.metadata

    if (halfDay) {
      if (approved) {
        return i18n.t('calendar.until_half_day', params)
      } else {
        return i18n.t('calendar.pending_half_day', params)
      }
    }

    if (!approved) {
      return i18n.t('calendar.pending', params)
    }

    if (!finishOn) {
      return i18n.t('calendar.until_no_finish_date', params)
    }

    return i18n.t('calendar.until', params)
  }

  const leaveEvent = (event: CalendarEvent) => {
    const empId = event.metadata('employee_id')
    const emp = employeeLookup.get(empId)

    if (!emp) return

    return (
      <div className={styles.event} key={event.id}>
        <div className={styles.emoji}>
          <UserAvatar
            user={{ avatar: emp.access.avatar ?? '', fullName: emp.fullName }}
            size="s32"
          />
        </div>
        <div className={styles.description}>
          {employeeToName(emp)}
          &nbsp;
          {leaveCopy(event)}
        </div>
      </div>
    )
  }

  const leaveEvents = (events: CalendarEvent[] | null | undefined) => {
    if (!events) return null

    if (isEmpty(events)) return null

    return <Bubble overflowLimit={2}>{events.map(leaveEvent)}</Bubble>
  }

  const groupedEvents = () => {
    const filteredCalendarEvents = uniqBy(calendarEvents, (event) => {
      switch (event.get('type')) {
        case 'leave':
          return `leave-${leaveType(event)}-${event.get('metadata').employee_id}`

        default:
          return event.get('id')
      }
    })

    const groups = groupBy(filteredCalendarEvents, (event) => {
      switch (event.get('type')) {
        case 'leave':
          return `leave-${leaveType(event)}`

        case 'workiversary':
          return 'workiversary'

        default:
          return 'other'
      }
    })

    return groups
  }

  const groupedEventsInstance = groupedEvents()

  return (
    <div>
      {otherEvents(groupedEventsInstance.other)}
      {workiversaryEvents(groupedEventsInstance.workiversary)}
      {leaveEvents(groupedEventsInstance['leave-holiday'])}
      {leaveEvents(groupedEventsInstance['leave-sick'])}
      {leaveEvents(groupedEventsInstance['leave-other'])}
      {leaveEvents(groupedEventsInstance['leave-default'])}
    </div>
  )
}

export default DayEventsDetail
